"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { commonApiFetch } from "@/services/api/common-api";
import { sanitizeErrorForUser } from "@/utils/error-sanitizer";
import { ProcessedContent, isVideoMimeType, processContent } from "./media-utils";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
interface UseDropContentResult {
  drop: ApiDrop | null;
  content: ProcessedContent;
  isLoading: boolean;
  error: unknown;
}

/**
 * Custom hook to fetch and process drop content
 */
export const useDropContent = (dropId, dropPartId, maybeDrop) => {
  const $ = _c(28);
  let t0;
  let t1;
  if ($[0] !== dropId) {
    t0 = [QueryKey.DROP, {
      drop_id: dropId
    }];
    t1 = async () => await commonApiFetch({
      endpoint: `drops/${dropId}`
    });
    $[0] = dropId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = maybeDrop ?? undefined;
  const t3 = !maybeDrop;
  let t4;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t1,
      placeholderData: keepPreviousData,
      initialData: t2,
      enabled: t3
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: drop,
    isFetching,
    error
  } = useQuery(t4);
  let t5;
  if ($[8] !== drop || $[9] !== dropPartId || $[10] !== error || $[11] !== isFetching || $[12] !== maybeDrop) {
    t5 = () => {
      if (isFetching && !maybeDrop) {
        return {
          segments: [{
            type: "text",
            content: "Loading..."
          }],
          apiMedia: []
        };
      }
      if (error) {
        const regex = /Drop [0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12} not found/;
        const sanitizedError = sanitizeErrorForUser(error);
        const errorMsg = regex.test(sanitizedError) ? "This drop has been deleted by the author" : sanitizedError;
        return {
          segments: [{
            type: "text",
            content: errorMsg
          }],
          apiMedia: []
        };
      }
      if (!drop) {
        return {
          segments: [],
          apiMedia: []
        };
      }
      const part_0 = drop.parts.find(part => part.part_id === dropPartId);
      if (!part_0) {
        return {
          segments: [],
          apiMedia: []
        };
      }
      const apiMedia = (part_0.media || []).map(_temp);
      if (!part_0.content) {
        return {
          segments: apiMedia.length ? [] : [{
            type: "text",
            content: "Media"
          }],
          apiMedia
        };
      }
      return processContent(part_0.content, apiMedia);
    };
    $[8] = drop;
    $[9] = dropPartId;
    $[10] = error;
    $[11] = isFetching;
    $[12] = maybeDrop;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const getContent = t5;
  let t6;
  if ($[14] !== getContent) {
    t6 = getContent();
    $[14] = getContent;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const [content, setContent] = useState(t6);
  let t7;
  if ($[16] !== getContent) {
    t7 = () => {
      setContent(getContent());
    };
    $[16] = getContent;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== drop || $[19] !== dropPartId || $[20] !== error || $[21] !== isFetching) {
    t8 = [drop, dropPartId, isFetching, error];
    $[18] = drop;
    $[19] = dropPartId;
    $[20] = error;
    $[21] = isFetching;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  useEffect(t7, t8);
  const t9 = drop ?? null;
  const t10 = isFetching && !maybeDrop;
  let t11;
  if ($[23] !== content || $[24] !== error || $[25] !== t10 || $[26] !== t9) {
    t11 = {
      drop: t9,
      content,
      isLoading: t10,
      error
    };
    $[23] = content;
    $[24] = error;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
function _temp(media) {
  return {
    alt: "Media",
    url: media.url,
    type: (isVideoMimeType(media.mime_type) ? "video" : "image") as "video" | "image"
  };
}